import { DocumentServicesObject, PageRef } from '@wix/document-services-types';
import { IntentInstallOptions } from '@wix/editor-platform-host-integration-apis';
import {
  PlatformApiMethods,
  PlatformContext,
} from '../../../../types/platformApi';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { createDependenciesDriver } from '../../appDependenciesDriver';
import { IntentInstallStore } from '../IntentInstallStore';
import { ProgressBarManager } from '../ProgressBarManager';
import {
  AFTER_INSTALL_APP_HISTORY_LABEL,
  BEFORE_INSTALL_APP_HISTORY_LABEL,
} from '../constants';
import { InstallationStrategy } from './types';
import { BaseInstallationStrategy } from './BaseInstallationStrategy';

export class SerialInstallationStrategy implements InstallationStrategy {
  private baseInstallStrategy: BaseInstallationStrategy;

  constructor(
    private platformApiMethods: PlatformApiMethods,
    private documentServicesAPI: DocumentServicesObject,
    private experimentsAPI: IExperimentsApiAdapter,
    private intentInstallStore: IntentInstallStore,
    private progressBarManager: ProgressBarManager,
  ) {
    this.baseInstallStrategy = new BaseInstallationStrategy(
      this.platformApiMethods,
      this.documentServicesAPI,
      this.experimentsAPI,
      this.intentInstallStore,
      this.progressBarManager,
    );
  }

  private async navigateToApp(appDefinitionId: string) {
    const allPages = this.documentServicesAPI.pages.getPagesData();
    const pageId = allPages.find((page) => {
      const isPageVisible = !page.hidePage;
      const isPageBelongsToCurrentApp =
        page.appDefinitionId === appDefinitionId;
      return isPageBelongsToCurrentApp && isPageVisible;
    })?.id;
    if (!pageId) {
      return;
    }
    const pageRef = this.documentServicesAPI.pages.getReference(
      pageId,
    ) as PageRef;

    const appData =
      this.documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId);

    await this.platformApiMethods.document.pages.navigateTo(appData, '', {
      pageRef,
    });
  }

  public async install(
    context: PlatformContext,
    apps: string[],
    options: IntentInstallOptions,
  ): Promise<void> {
    for (const appDefId of apps) {
      await context.platformApiMethods.document.history.add({}, '', {
        label: BEFORE_INSTALL_APP_HISTORY_LABEL + appDefId,
      });

      try {
        await this.installAppDependencies(context, appDefId, apps);
        await this.baseInstallStrategy.install(context, [appDefId], options, {
          shouldUpdateProgressBar: true,
        });
        await context.platformApiMethods.document.history.add({}, '', {
          label: AFTER_INSTALL_APP_HISTORY_LABEL + appDefId,
        });
        if (!this.intentInstallStore.isSilent()) {
          await this.navigateToApp(appDefId);
        }
      } catch (err) {
        this.intentInstallStore.addAppWithError(appDefId);
        await context.platformApiMethods.document.history.performUndoUntilLabel(
          {},
          '',
          { snapshotLabel: BEFORE_INSTALL_APP_HISTORY_LABEL + appDefId },
        );
      }
    }
  }

  private async installAppDependencies(
    context: PlatformContext,
    appDefId: string,
    appDefIds: string[],
  ) {
    const dependenciesDriver = await createDependenciesDriver(
      this.documentServicesAPI,
      appDefId,
    );

    const { appsToInstall, dependencyOptions } =
      dependenciesDriver.getAppsToInstall();

    const dependenciesToInstall = appsToInstall.filter(
      (dependencyAppDefId) =>
        dependencyAppDefId !== appDefId &&
        !appDefIds.includes(dependencyAppDefId),
    );

    for (const dependencyAppDefId of dependenciesToInstall) {
      await this.baseInstallStrategy.install(context, [dependencyAppDefId], {
        appsOptions: dependencyOptions,
      });
    }
  }
}
